import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import './LegalGate.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LegalGate — Redirector full-screen.
 *
 * 🟥 CUADRO NEGRO 2026-06-03 (mobile UX rework):
 * En vez de mostrar un modal con ambos documentos, ahora redirige al
 * usuario al flujo secuencial /terminos → /privacidad. Cada página muestra
 * un checkbox debajo del footer NY. Al marcar checkbox en /terminos navega
 * a /privacidad; al marcar checkbox en /privacidad se envía POST
 * /api/legal/accept y se vuelve al dashboard "/".
 *
 *   GET  /api/legal/status   → { ok, accepted, accepted_version, current_version }
 *   POST /api/legal/accept   → { ok, accepted, accepted_version, current_version }
 */
export default function LegalGate({ isAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();

  const checkStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    // No interceptar si el usuario YA está en /terminos o /privacidad (puede
    // estar leyéndolos manualmente o transitando el flujo de aceptación).
    const p = location.pathname || '';
    if (p.startsWith('/terminos') || p.startsWith('/privacidad')) return;
    try {
      const res = await axios.get(`${API_URL}/legal/status`, { withCredentials: true });
      const data = res.data || {};
      const isAccepted = !!data.accepted;
      const accV = data.accepted_version ?? 0;
      const curV = data.current_version ?? 0;
      const revoked = !!data.consent_revoked;
      const shouldShow = !isAccepted || accV < curV || revoked;
      if (shouldShow) {
        // Redirigir al flujo secuencial: empieza por Términos.
        navigate('/terminos?gate=1', { replace: true });
      }
    } catch {
      // 401 u otro error: no forzar redirección.
    }
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // Interceptor: si el backend devuelve 403 LEGAL_GATE_REQUIRED → redirigir.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const code = err?.response?.data?.error;
        if (status === 403 && (code === 'LEGAL_GATE_REQUIRED' || code === 'LEGAL_GATE_REVOKED')) {
          const p = window.location.pathname || '';
          if (!p.startsWith('/terminos') && !p.startsWith('/privacidad')) {
            navigate('/terminos?gate=1', { replace: true });
          }
        }
        return Promise.reject(err);
      },
    );
    return () => { axios.interceptors.response.eject(id); };
  }, [navigate]);

  return null;
}

